import { createSysdyneId } from '@sysdynetechnologies/sysdyne-id-client-frontend';
import type {
  SysdyneIdUser,
  LogoutOptions,
} from '@sysdynetechnologies/sysdyne-id-client-frontend';
import type {
  SysdyneIdPluginOptions,
  SysdyneIdPlugin,
  AuthAppState,
  AppRedirectLoginOptions,
} from './types';

function urljoin(...paths: (string | undefined)[]): string {
  const path = paths
    .filter((path) => path !== undefined)
    .map((path) => path?.replace(/^\/+|\/+$/g, ''))
    .join('/');
  return new URL(path, window.location.origin).pathname;
}

export function createSysdyneIdPlugin(options: SysdyneIdPluginOptions): SysdyneIdPlugin {
  const {
    clientOptions,
    baseUrl = '/',
    callbackPath = '/sso',
  } = options;

  const redirectUri = urljoin(baseUrl, callbackPath);

  const client = createSysdyneId<AuthAppState>({
    ...clientOptions,
    authorizationParams: {
      ...clientOptions.authorizationParams,
      redirectUri,
    },
  });

  const isAuthenticated = async (): Promise<boolean> => {
    return client.isAuthenticated();
  };

  const getUser = async (): Promise<SysdyneIdUser | undefined> => {
    return await client.getUser();
  };

  const refreshState = async () => {
    const authenticated = await client.isAuthenticated();
    const currentUser = await client.getUser();
    options.onAuthStateChanged?.(authenticated, currentUser);
  };

  const proxyClientCall = async <T,>(fn: () => Promise<T> | T): Promise<T> => {
    let res: T;
    try {
      res = await fn();
    } finally {
      await refreshState();
    }
    return res;
  };

  const checkSession = () => {
    return refreshState();
  };

  const getToken = (forceRefresh = false) => {
    return proxyClientCall(() => client.getToken(forceRefresh));
  };

  const getAccessToken = () => {
    return proxyClientCall(() => client.getAccessToken());
  };

  const loginWithRedirect = (opts?: AppRedirectLoginOptions) => {
    const url = new URL(window.location.href);
    const pathname = url.pathname.replace(baseUrl ?? '/', '/');
    const returnUrl = pathname + url.search + url.hash;

    return proxyClientCall(() =>
      client.loginWithRedirect({
        ...opts,
        appState: {
          returnUrl,
          ...opts?.appState,
        },
      }),
    );
  };

  const handleRedirectCallback = async () => {
    return proxyClientCall(() => client.handleRedirectCallback());
  };

  const logout = (opts?: LogoutOptions): Promise<string> => {
    const options: LogoutOptions = {
      returnTo: baseUrl,
      ...opts,
    };
    return client.logout(options);
  };

  const isOnRedirectPage = () => {
    return client.isRedirectCallback(window.location.href);
  };

  // On mount, check if we need to handle a redirect callback
  (async () => {
    if (isOnRedirectPage()) {
      try {
        const { appState } = await handleRedirectCallback();
        options.onPostRedirectNavigateRequest(appState?.returnUrl ?? '/');
      } catch (error) {
        options.onRedirectError?.(error);
      }
    } else {
      await refreshState();
    }
  })();

  return {
    isAuthenticated,
    isOnRedirectPage,
    getUser,
    checkSession,
    getToken,
    getAccessToken,
    loginWithRedirect,
    handleRedirectCallback,
    logout,
  };
}
